import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Disc3, ArrowLeft } from "lucide-react";
import { useAuth } from "../auth";
import { useLang } from "../i18n";
import api, { formatApiError } from "../api";

const Auth = () => {
  const { t } = useLang();
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [hp, setHp] = useState("");
  const back = params.get("return") || "/dashboard";

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email, website: hp });
        setNotice(t("auth_forgot_sent"));
      } else if (mode === "register") {
        const u = await register(form.name, form.email, form.password, hp);
        navigate(u?.email_verified ? (params.get("return") || (u?.role === "admin" ? "/bacheca" : "/dashboard")) : "/verifica-email");
      } else {
        const u = await login(form.email, form.password);
        navigate(params.get("return") || (u?.role === "admin" ? "/bacheca" : "/dashboard"));
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-lg border border-white/15 bg-black/40 px-4 py-3 text-sm placeholder:text-mute focus:border-ember focus:outline-none transition-colors";

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center px-5" data-testid="auth-page">
      <div className="orb absolute top-10 left-10 w-96 h-96 rounded-full bg-ember/15" />
      <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-card p-8 sm:p-10">
        <Link to={back} className="inline-flex items-center gap-2 font-mono2 text-[10px] tracking-[0.25em] uppercase text-mute hover:text-ember transition-colors mb-8">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("story_back")}
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <Disc3 className="w-6 h-6 text-ember" />
          <h1 className="font-display font-bold text-2xl" data-testid="auth-title">
            {mode === "register" ? t("auth_register_title") : t("auth_login_title")}
          </h1>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-4">
          {mode === "register" && (
            <input data-testid="auth-name-input" required className={field} placeholder={t("auth_name")}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          )}
          <input data-testid="auth-email-input" required type="email" className={field} placeholder={t("auth_email")}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          {mode !== "forgot" && (
            <input data-testid="auth-password-input" required type="password" minLength={6} className={field} placeholder={t("auth_password")}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          )}
          {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}
          {notice && <p data-testid="auth-notice" className="text-sm text-gold">{notice}</p>}
          <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }} />
          <button data-testid="auth-submit-button" disabled={busy}
            className="w-full rounded-full bg-ember py-3.5 text-sm font-semibold uppercase tracking-wider text-black hover:bg-gold transition-colors disabled:opacity-50">
            {mode === "register" ? t("auth_register_btn") : mode === "forgot" ? t("auth_forgot_btn") : t("auth_login_btn")}
          </button>
        </form>
        <div className="flex items-center gap-4 my-6">
          <span className="flex-1 h-px bg-white/10" />
          <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-mute">{t("auth_or")}</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
        <button data-testid="auth-google-button" onClick={() => { sessionStorage.setItem("auth_return", back); loginWithGoogle(); }}
          className="w-full rounded-full border border-white/20 py-3.5 text-sm font-semibold uppercase tracking-wider hover:border-ember hover:text-ember transition-colors">
          {t("auth_google")}
        </button>
        <div className="mt-6 text-center space-y-2 text-sm">
          {mode !== "forgot" ? (
            <>
              <button data-testid="auth-forgot-link" onClick={() => setMode("forgot")} className="block w-full text-mute hover:text-gold transition-colors">
                {t("auth_forgot")}
              </button>
              <button data-testid="auth-switch-mode" onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-mist hover:text-ember transition-colors">
                {mode === "register" ? t("auth_have") : t("auth_none")}
              </button>
            </>
          ) : (
            <>
              <button data-testid="auth-back-login" onClick={() => setMode("login")} className="block w-full text-mist hover:text-ember transition-colors">
                {t("auth_have")}
              </button>
              <Link to="/recupera" data-testid="auth-recovery-link" className="block text-mute hover:text-gold transition-colors">
                {t("auth_recovery_link")}
              </Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default Auth;
